/**
 * TOWER PERSISTENCE — SAVE / EXPORT / IMPORT / SHARE.
 *
 * One self-contained module for getting a `TowerDesign` in and out of the
 * browser: local storage (namespaced), file download/upload, and shareable
 * URLs. Everything is pure logic or a small DOM utility — no framework, no
 * globals beyond `window`/`document`/`localStorage`, all guarded so malformed
 * input yields a friendly `{ error }` rather than a throw.
 *
 * A `TowerDesign` (see src/game/towerSpec.ts) is plain JSON: `{ id, name,
 * description, author?, spec }`. Every load path runs it back through
 * `validateTower`, so nothing that fails validation is ever handed to the game.
 *
 * WIRING (maintainer notes)
 * -------------------------
 * Save button:     persistence.saveLocal(currentDesign)
 * List saved:      const saved = persistence.listLocal()   // for a "Load" menu
 * Load one:        const d = persistence.loadLocal(id); if (d) load(d)
 * Delete:          persistence.deleteLocal(id)
 * Export button:   persistence.exportFile(currentDesign)   // downloads <id>.json
 * Import button:   const { design, error } = await persistence.importFile()
 *                  if (error) showError(error); else load(design!)
 * Share button:    const url = persistence.encodeShareUrl(currentDesign)
 *                  navigator.clipboard.writeText(url)       // or set location.hash
 * Load-on-start:   const res = persistence.decodeShareUrl()  // reads location.hash
 *                  if (res?.design) load(res.design)
 *                  else if (res?.error) showError(res.error)
 *                  // res === null  → no share param, start normally
 *
 * `saveLocal`/`exportFile`/`encodeShareUrl` never invent ids — the caller owns
 * `design.id`. No Math.random / argless Date is used anywhere here.
 */
import { type TowerDesign, validateTower } from "@/game/towerSpec";

/** Namespace for every local-storage key this module writes. */
export const STORAGE_PREFIX = "dfodgs.towers.";

/** Share-link hash parameter, e.g. `#t=<base64url>`. */
export const SHARE_PARAM = "t";

/**
 * Rough ceiling for a share payload before browsers/servers start truncating
 * URLs (~2000 chars is the safe cross-browser limit; leave headroom for the
 * origin + `#t=`). `encodeShareUrl` still returns the URL but flags oversize.
 */
export const SHARE_URL_SOFT_LIMIT = 1800;

// ---------------------------------------------------------------------------
// Serialize / parse
// ---------------------------------------------------------------------------

/** Pretty-print a design to JSON (2-space indent). */
export function serializeDesign(d: TowerDesign): string {
  return JSON.stringify(d, null, 2);
}

/** Narrow unknown JSON to something that could plausibly be a TowerDesign. */
function isDesignShape(v: unknown): v is TowerDesign {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.name === "string" &&
    typeof o.description === "string" &&
    typeof o.spec === "object" &&
    o.spec !== null
  );
}

/**
 * Parse a JSON string into a validated `TowerDesign`. Returns `{ design }` on
 * success, or `{ error }` with a human-readable reason (bad JSON, wrong shape,
 * or a validation failure listing the offending fields).
 */
export function parseDesign(json: string): { design?: TowerDesign; error?: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (e) {
    return { error: `Not valid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }
  if (!isDesignShape(raw)) {
    return { error: "JSON is not a tower design (needs id, name, description, spec)." };
  }
  const result = validateTower(raw);
  if (!result.ok) {
    return { error: `Invalid tower: ${result.errors.join("; ")}` };
  }
  return { design: raw };
}

// ---------------------------------------------------------------------------
// Local storage
// ---------------------------------------------------------------------------

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    // Accessing localStorage can throw (privacy mode, sandboxed iframe).
    return null;
  }
}

function keyFor(id: string): string {
  return STORAGE_PREFIX + id;
}

/**
 * Save a design under `dfodgs.towers.<id>`, overwriting any existing entry with
 * the same id. Storage errors (quota, disabled storage) are swallowed — call
 * `loadLocal(id)` afterwards if you need to confirm it stuck.
 */
export function saveLocal(d: TowerDesign): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(keyFor(d.id), serializeDesign(d));
  } catch {
    // Quota exceeded or storage unavailable — nothing more we can do.
  }
}

/**
 * Every saved design that still parses + validates, sorted by name. Corrupt or
 * now-invalid entries are skipped rather than throwing.
 */
export function listLocal(): TowerDesign[] {
  const s = storage();
  if (!s) return [];
  const out: TowerDesign[] = [];
  let n = 0;
  try {
    n = s.length;
  } catch {
    return [];
  }
  for (let i = 0; i < n; i++) {
    let k: string | null = null;
    try {
      k = s.key(i);
    } catch {
      continue;
    }
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    let json: string | null = null;
    try {
      json = s.getItem(k);
    } catch {
      continue;
    }
    if (!json) continue;
    const { design } = parseDesign(json);
    if (design) out.push(design);
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/** Load one saved design by id, or `undefined` if missing/corrupt/invalid. */
export function loadLocal(id: string): TowerDesign | undefined {
  const s = storage();
  if (!s) return undefined;
  let json: string | null = null;
  try {
    json = s.getItem(keyFor(id));
  } catch {
    return undefined;
  }
  if (!json) return undefined;
  return parseDesign(json).design;
}

/** Remove a saved design by id. No-op if storage is unavailable. */
export function deleteLocal(id: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(keyFor(id));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// File export / import
// ---------------------------------------------------------------------------

/** Turn an id into a safe `.json` filename. */
function fileNameFor(id: string): string {
  const safe = id.replace(/[^a-z0-9-]+/gi, "-").replace(/^-+|-+$/g, "") || "tower";
  return `${safe}.json`;
}

/**
 * Trigger a browser download of the design as `<id>.json`. Uses a Blob +
 * object URL and a transient `<a download>`; the URL is revoked afterwards.
 * No-op outside a DOM environment.
 */
export function exportFile(d: TowerDesign): void {
  if (typeof document === "undefined" || typeof URL === "undefined" || typeof Blob === "undefined") {
    return;
  }
  const blob = new Blob([serializeDesign(d)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileNameFor(d.id);
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Give the click a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Open a hidden `<input type=file>`, let the user pick a `.json`, then read +
 * parse it. Resolves with `{ design }` or `{ error }`. If the user cancels the
 * picker the promise never resolves (standard file-input behaviour) — callers
 * that need cancel detection can race it against their own UI.
 */
export function importFile(): Promise<{ design?: TowerDesign; error?: string }> {
  return new Promise((resolve) => {
    if (typeof document === "undefined" || typeof FileReader === "undefined") {
      resolve({ error: "File import is not available in this environment." });
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.style.display = "none";

    const cleanup = () => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.addEventListener("change", () => {
      const file = input.files && input.files[0];
      if (!file) {
        cleanup();
        resolve({ error: "No file selected." });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        cleanup();
        const text = typeof reader.result === "string" ? reader.result : "";
        resolve(parseDesign(text));
      };
      reader.onerror = () => {
        cleanup();
        resolve({ error: "Could not read the selected file." });
      };
      reader.readAsText(file);
    });

    document.body.appendChild(input);
    input.click();
  });
}

// ---------------------------------------------------------------------------
// Share link
// ---------------------------------------------------------------------------

/** UTF-8 safe base64url encode (no padding, `+/` → `-_`). */
function toBase64Url(text: string): string {
  // encodeURIComponent → %XX escapes → bytes, so non-ASCII survives btoa.
  const bytes = encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_m, h: string) =>
    String.fromCharCode(parseInt(h, 16)),
  );
  const b64 = btoa(bytes);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Inverse of {@link toBase64Url}. Throws on malformed input (caller guards). */
function fromBase64Url(b64url: string): string {
  let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4 !== 0) b64 += "=";
  const bytes = atob(b64);
  // Reverse the encodeURIComponent trick.
  let pct = "";
  for (let i = 0; i < bytes.length; i++) {
    const h = bytes.charCodeAt(i).toString(16).padStart(2, "0");
    pct += "%" + h;
  }
  return decodeURIComponent(pct);
}

/**
 * Build a share URL whose hash carries the whole design as base64url JSON, e.g.
 * `https://host/path#t=<payload>`. Compact JSON (no indentation) keeps it short.
 * If the result exceeds {@link SHARE_URL_SOFT_LIMIT} a `console.warn` notes that
 * some browsers/servers may truncate it — the URL is still returned.
 */
export function encodeShareUrl(d: TowerDesign): string {
  const payload = toBase64Url(JSON.stringify(d));
  const base =
    typeof location !== "undefined"
      ? location.origin + location.pathname + location.search
      : "";
  const url = `${base}#${SHARE_PARAM}=${payload}`;
  if (url.length > SHARE_URL_SOFT_LIMIT) {
    // Too large is not fatal, but worth flagging to the maintainer.
    // eslint-disable-next-line no-console
    console.warn(
      `Share URL is ${url.length} chars (> ${SHARE_URL_SOFT_LIMIT}); it may be truncated by some browsers/servers. Prefer file export for large designs.`,
    );
  }
  return url;
}

/**
 * Decode a design from a URL hash. Pass an explicit `hash` (e.g.
 * `"#t=..."` or the raw payload's containing string) or omit it to read
 * `location.hash`. Returns:
 *   - `null`          → no `t=` share param present (start normally)
 *   - `{ design }`    → decoded + validated design
 *   - `{ error }`     → param present but corrupt / not a valid tower
 */
export function decodeShareUrl(
  hash?: string,
): { design?: TowerDesign; error?: string } | null {
  const raw = hash ?? (typeof location !== "undefined" ? location.hash : "");
  if (!raw) return null;
  // Accept "#t=...", "t=...", "#a=1&t=...", or a bare payload after "#".
  const h = raw.startsWith("#") ? raw.slice(1) : raw;
  const params = new URLSearchParams(h);
  const payload = params.get(SHARE_PARAM);
  if (!payload) return null;
  let json: string;
  try {
    json = fromBase64Url(payload);
  } catch {
    return { error: "Share link payload is malformed and could not be decoded." };
  }
  return parseDesign(json);
}
